import { useState } from 'react';
import {
  cartLineKey,
  type CartLine,
  type OeSealCartLine,
  type SelectedPosition,
} from '../../api/types.js';
import { GuidedSelection } from '../../components/GuidedSelection.js';
import { FreeTextSearch } from '../../components/FreeTextSearch.js';
import { OeConfigurator } from '../../components/OeConfigurator.js';
import { SelectionList } from '../../components/SelectionList.js';
import { QuotePanel } from '../../components/QuotePanel.js';
import { Field, PageHeader } from '../shared/ui.js';

/**
 * Schnellangebot fuer die berechtigten internen Rollen (`sales`,
 * `operator_admin`), einschliesslich Aussendienst.
 *
 * Fuer einen Kunden, der STAR selbst nicht nutzt -- der Aussendienst beim Kunden
 * oder am Telefon soll schneller als ueber das ERP einen belastbaren Preis nennen
 * koennen. Bewusst schmal: **kein Konto**, kein CRM, kein neuer Kundenstamm, keine
 * zweite Preisengine. Firmenname und Kundennummer sind reiner Belegtext
 * (`recipientName`/`recipientNumber` am Angebot); es entsteht dabei kein
 * `Account`. Der Rabatt ist ein frei vergebener Satz (`discountPercent`,
 * serverseitig auf 0 bis 40 Prozent geprueft, siehe `createQuoteSchema`) -- keine
 * Kondition, keine Rabattgruppe, kein Konto liefert ihn.
 *
 * Danach die unveraenderte Artikelauswahl (gefuehrt oder Freitext), derselbe
 * Warenkorb und dasselbe `QuotePanel` wie im Kundenbereich -- inklusive Preview,
 * Entwurf, Ausstellen und PDF. **G-ST-P/OE ist eingebunden**, ueber denselben
 * `OeConfigurator` wie im Kundenbereich (ADR-0022) -- kein zweiter Konfigurator,
 * keine zweite OE-Logik, nur der freie Rabattsatz zusaetzlich durchgereicht. Die
 * OE-**Preisverwaltung** (`/admin/oe-preise`) bleibt davon unberuehrt: Sie ist
 * weiterhin ausschliesslich `operator_admin` vorbehalten, siehe `AdminShell`.
 */
export function QuickQuotePage(): React.JSX.Element {
  const [firma, setFirma] = useState('');
  const [kundennummer, setKundennummer] = useState('');
  const [rabatt, setRabatt] = useState('');
  const [tab, setTab] = useState<'guided' | 'search' | 'oe'>('guided');
  const [positions, setPositions] = useState<readonly CartLine[]>([]);
  const [freight, setFreight] = useState('');
  const [quoteOffen, setQuoteOffen] = useState(false);
  /** Siehe CustomerApp: erzwingt einen Neuaufbau des OE-Konfigurators bei
   *  "Neues Angebot beginnen", da sein Eingabe- und Vorschau-State
   *  unabhaengig vom Warenkorb ist. */
  const [oeResetNonce, setOeResetNonce] = useState(0);

  const rabattZahl = rabatt.trim() === '' ? null : Number(rabatt);
  const rabattGueltig =
    rabattZahl !== null &&
    !Number.isNaN(rabattZahl) &&
    rabattZahl >= 0 &&
    rabattZahl <= 40;
  const bereit = firma.trim() !== '' && rabattGueltig;

  /** Gleicher Artikel zweimal uebernommen: Mengen werden addiert, nicht ersetzt.
   *  Vorhandene OE-Zeilen bleiben dabei unangetastet erhalten, siehe `addOeLine`. */
  function addPositions(neue: readonly SelectedPosition[]): void {
    setPositions((prev) => {
      const byArticle = new Map(
        prev
          .filter((p): p is SelectedPosition => 'articleId' in p)
          .map((p) => [p.articleId, p]),
      );
      for (const n of neue) {
        const vorhanden = byArticle.get(n.articleId);
        byArticle.set(
          n.articleId,
          vorhanden === undefined
            ? n
            : { ...n, quantity: String(Number(vorhanden.quantity) + Number(n.quantity)) },
        );
      }
      const oeZeilen = prev.filter((p): p is OeSealCartLine => !('articleId' in p));
      return [...byArticle.values(), ...oeZeilen];
    });
  }

  /** Jede konfigurierte Dichtung ist eine eigene Zeile -- anders als beim
   *  Katalogartikel gibt es keine Zusammenfuehrung ueber eine Artikelnummer,
   *  derselbe Grundsatz wie im Kundenbereich (`CustomerApp`). */
  function addOeLine(line: OeSealCartLine): void {
    setPositions((prev) => [...prev, line]);
  }

  /** Neues Schnellangebot: bewusst auch Firma und Rabatt leeren, damit die
   *  naechste Fassung nicht versehentlich den vorigen Kunden traegt. */
  function neuBeginnen(): void {
    setFirma('');
    setKundennummer('');
    setRabatt('');
    setPositions([]);
    setFreight('');
    setQuoteOffen(false);
    setOeResetNonce((n) => n + 1);
  }

  return (
    <>
      <PageHeader
        title="Schnellangebot"
        lead="Fuer einen Kunden, der STAR nicht selbst nutzt -- Firma, Rabatt, dann Artikel wie gewohnt."
      />
      <section className="card">
        <div className="grid2">
          <Field label="Firma *">
            <input
              type="text"
              required
              maxLength={200}
              value={firma}
              onChange={(e) => setFirma(e.target.value)}
              placeholder="z. B. Mueller Rohrleitungsbau GmbH"
            />
          </Field>
          <Field label="Kundennummer" hint="optional, z. B. aus dem ERP">
            <input
              type="text"
              maxLength={40}
              value={kundennummer}
              onChange={(e) => setKundennummer(e.target.value)}
            />
          </Field>
          <Field label="Rabatt in % *" hint="0 bis 40, frei vergeben.">
            <input
              type="number"
              required
              min={0}
              max={40}
              step="0.5"
              value={rabatt}
              onChange={(e) => setRabatt(e.target.value)}
            />
          </Field>
        </div>
      </section>

      {bereit && (
        <div className="layout">
          <div>
            <nav className="tabs" aria-label="Einstieg">
              <button
                type="button"
                className={tab === 'guided' ? 'tab selected' : 'tab'}
                aria-current={tab === 'guided'}
                onClick={() => setTab('guided')}
              >
                Gefuehrte Auswahl
              </button>
              <button
                type="button"
                className={tab === 'search' ? 'tab selected' : 'tab'}
                aria-current={tab === 'search'}
                onClick={() => setTab('search')}
              >
                Freitextsuche
              </button>
              <button
                type="button"
                className={tab === 'oe' ? 'tab selected' : 'tab'}
                aria-current={tab === 'oe'}
                onClick={() => setTab('oe')}
              >
                G-ST-P/OE konfigurieren
              </button>
            </nav>

            {tab === 'guided' && <GuidedSelection onTakeOver={addPositions} />}
            {tab === 'search' && <FreeTextSearch onTakeOver={addPositions} />}
            {tab === 'oe' && (
              <OeConfigurator
                key={oeResetNonce}
                freight={freight}
                discountPercent={rabattZahl!}
                onTakeOver={addOeLine}
              />
            )}

            {(positions.length > 0 || quoteOffen) && (
              <QuotePanel
                positions={positions}
                freight={freight}
                recipient={{
                  name: firma.trim(),
                  discountPercent: rabattZahl!,
                  ...(kundennummer.trim() !== '' ? { number: kundennummer.trim() } : {}),
                }}
                onQuoteChange={setQuoteOffen}
                // Keine eigene Historienansicht hier: Ausgestellte Schnellangebote
                // stehen wie jedes andere Angebot in der zentralen Uebersicht
                // (/admin/angebote). Der Warenkorb wird trotzdem sofort geleert --
                // ein ausgestelltes Schnellangebot ist abgeschlossen, siehe
                // CustomerApp.
                onIssued={() => setPositions([])}
                onReset={neuBeginnen}
              />
            )}
          </div>

          <SelectionList
            positions={positions}
            freight={freight}
            discountPercent={rabattZahl!}
            onFreightChange={setFreight}
            onRemove={(key) =>
              setPositions((prev) => prev.filter((p) => cartLineKey(p) !== key))
            }
            onClear={() => setPositions([])}
          />
        </div>
      )}
    </>
  );
}
